import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaChartLine } from "react-icons/fa";
import { apihubPost } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

function toIstDateKey(unixSec) {
  const d = new Date(Number(unixSec) * 1000);
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function dateToUnixStart(dateStr) {
  return Math.floor(new Date(`${dateStr}T00:00:00+05:30`).getTime() / 1000);
}

function dateToUnixEnd(dateStr) {
  return Math.floor(new Date(`${dateStr}T23:59:59+05:30`).getTime() / 1000);
}

function hasActivityTimestamp(value) {
  return Number.isFinite(Number(value));
}

function normalizeAgentActivityResponse(res) {
  const nested = res?.data;
  const body =
    nested &&
    (Array.isArray(nested.intervenedata) ||
      Array.isArray(nested.resolveddata) ||
      Array.isArray(nested.autoresolveddata))
      ? nested
      : res;

  return {
    intervenedata: Array.isArray(body?.intervenedata) ? body.intervenedata : [],
    resolveddata: Array.isArray(body?.resolveddata) ? body.resolveddata : [],
    autoresolveddata: Array.isArray(body?.autoresolveddata)
      ? body.autoresolveddata
      : [],
  };
}

function aggregateActivity(intervenedata, resolveddata, autoresolveddata) {
  const byDate = {};

  const ensure = (key, ts) => {
    if (!byDate[key]) {
      byDate[key] = {
        ist_date: key,
        timestamp: ts,
        totalIntervene: 0,
        resolvedChats: 0,
        autoResolvedChats: 0,
      };
    }
    return byDate[key];
  };

  (intervenedata || []).forEach((row) => {
    const iv = row?.intervene;
    if (!iv?.isIntervened || !hasActivityTimestamp(iv.timestamp)) return;
    const ts = Number(iv.timestamp);
    const key = toIstDateKey(ts);
    ensure(key, ts).totalIntervene += 1;
  });

  (resolveddata || []).forEach((row) => {
    const rv = row?.resolve;
    if (!rv?.isResolved || !hasActivityTimestamp(rv.timestamp)) return;
    const ts = Number(rv.timestamp);
    const key = toIstDateKey(ts);
    ensure(key, ts).resolvedChats += 1;
  });

  (autoresolveddata || []).forEach((row) => {
    const rv = row?.resolve;
    const cust = row?.custid;
    if (!rv?.isResolved || !hasActivityTimestamp(rv.timestamp)) return;
    const ts = Number(rv.timestamp);
    const key = toIstDateKey(ts);
    const bucket = ensure(key, ts);
    bucket.autoResolvedChats += Array.isArray(cust) ? cust.length : 1;
  });

  return Object.values(byDate).sort((a, b) => a.timestamp - b.timestamp);
}

const AgentActivity = () => {
  const { projectId, phoneNoId } = useProjectPhone();
  const today = new Date().toISOString().split("T")[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];

  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [fromDate, setFromDate] = useState(weekAgo);
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!phoneNoId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apihubPost(
          `/getInteractedCustomerDetail/fetchallagentsbybusinessid/${phoneNoId}`,
          { projectId },
        );
        if (!cancelled && res?.success) {
          setAgents(res.data || []);
        }
      } catch {
        if (!cancelled) {
          setAgents([
            { _id: "69f86a1aecf68aefecc1e801", user_name: "Jas" },
            { _id: "69f5af382664fa3d3de64113", user_name: "Shailendra Singh" },
          ]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phoneNoId, projectId]);

  const fetchActivity = useCallback(async () => {
    if (!phoneNoId) return;
    if (new Date(fromDate) > new Date(toDate)) {
      setError("From date must be earlier or equal to To date");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const body = {
        bid: agentId || phoneNoId,
        phoneNoId,
        projectId,
        startsunix: dateToUnixStart(fromDate),
        endunix: dateToUnixEnd(toDate),
      };
      const res = await apihubPost(
        "/getInteractedCustomerDetail/getagentactivity",
        body,
      );
      const { intervenedata, resolveddata, autoresolveddata } =
        normalizeAgentActivityResponse(res);
      const hasRawData =
        intervenedata.length > 0 ||
        resolveddata.length > 0 ||
        autoresolveddata.length > 0;
      const aggregated = aggregateActivity(
        intervenedata,
        resolveddata,
        autoresolveddata,
      );
      setRows(aggregated);
      if (!hasRawData) {
        setError("No agent activity data for the selected range.");
      }
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch agent activity");
    } finally {
      setLoading(false);
    }
  }, [phoneNoId, agentId, fromDate, toDate, projectId]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity, tick]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          totalIntervene: acc.totalIntervene + r.totalIntervene,
          resolvedChats: acc.resolvedChats + r.resolvedChats,
          autoResolvedChats: acc.autoResolvedChats + r.autoResolvedChats,
        }),
        { totalIntervene: 0, resolvedChats: 0, autoResolvedChats: 0 },
      ),
    [rows],
  );

  const exportCsv = () => {
    const header = "Date,Total Intervene,Resolved,Auto Resolved\n";
    const lines = rows
      .map(
        (r) =>
          `${r.ist_date},${r.totalIntervene},${r.resolvedChats},${r.autoResolvedChats}`,
      )
      .join("\n");
    const blob = new Blob([header + lines], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agent_activity_${projectId || "project"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="biz-panel-page">
      <div className="biz-panel-header biz-panel-header-stack">
        <div>
          <h1>
            <FaChartLine />
            Agent Activity
          </h1>
          <p className="biz-panel-subtitle">
            Daily agent performance metrics — IST
          </p>
        </div>
        <div className="biz-activity-filters">
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            aria-label="Agent"
          >
            <option value="">Admin</option>
            {agents.map((a) => (
              <option key={a._id} value={a._id}>
                {a.user_name}
              </option>
            ))}
          </select>
          <label>
            From
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
          <button type="button" onClick={() => setTick((t) => t + 1)}>
            Apply
          </button>
          <button type="button" onClick={exportCsv} disabled={!rows.length}>
            Export CSV
          </button>
        </div>
      </div>

      {error && <p className="biz-panel-error">{error}</p>}

      <div className="biz-activity-grid">
        <div className="biz-activity-chart-card">
          <h3>Agent Activity Overview</h3>
          {loading ? (
            <p className="biz-table-empty">Loading…</p>
          ) : (
            <div className="biz-table-wrap">
              <table className="biz-table">
                <thead>
                  <tr>
                    <th>Date (IST)</th>
                    <th>Intervene</th>
                    <th>Resolved</th>
                    <th>Auto Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="biz-table-empty">
                        No data
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.ist_date}>
                        <td>{r.ist_date}</td>
                        <td>{r.totalIntervene}</td>
                        <td>{r.resolvedChats}</td>
                        <td>{r.autoResolvedChats}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="biz-activity-totals">
          <h3>Totals</h3>
          <dl>
            <div>
              <dt>Total Intervene</dt>
              <dd>{totals.totalIntervene}</dd>
            </div>
            <div>
              <dt>Total Resolved</dt>
              <dd>{totals.resolvedChats}</dd>
            </div>
            <div>
              <dt>Auto Resolved</dt>
              <dd>{totals.autoResolvedChats}</dd>
            </div>
          </dl>
          <small>Data timezone: Asia/Kolkata (IST)</small>
        </div>
      </div>
    </div>
  );
};

export default AgentActivity;
